/**
 * The tutorial's kitchen, as a literal.
 *
 * The sandbox runs the real engine — `initialState`, `tick`, the whole section-5
 * interaction table — and the engine reads content rather than constants. On the
 * server that content comes off disk; there is no disk here, and fetching it
 * would make the tutorial fail when the network does, which is precisely the
 * moment a player most wants something to do. So the kitchen ships in the
 * bundle: a map in the same glyph language `maps/*.txt` uses, parsed by the same
 * `parseMap`, and a recipe book and a timings file in the same shapes
 * `recipes.json` and `timings.json` load into.
 *
 * It is deliberately *not* a copy of a shipped preset. A tutorial kitchen has
 * different obligations: one seat, one dish, everything within a few steps, and
 * clocks short enough that waiting for a pan is a lesson rather than a delay.
 * What it must not differ on is the rules, and it does not — they are the same
 * package the server imports.
 *
 * `tutorialSandbox.test.ts` runs it through `validateMap` and `validateContent`,
 * the same two gates every shipped preset passes at boot, so a kitchen a lone
 * chef could not finish a dish in fails here rather than in front of a learner.
 */

import type { ClientConfig } from '@overcooked/shared'
import type { Content, MapContent, RecipeContent, TimingsContent } from '@overcooked/engine'
import { dishTicket, ingredientCatalogue, orderPoolFor, parseMap } from '@overcooked/engine'

/**
 * One floor region, one spawn, the chain down the two walls.
 *
 * Preparation on the left — two crates, board, counter, bin, plate return —
 * and service on the right — stove, plates, hatch, sink, extinguisher. Four
 * columns of floor between them, so the chef can watch a pan while walking for
 * a plate, and every fixture is faceable from the one region.
 *
 * Two crates, not one, because the pan holds a single ingredient now: a dish
 * with two things on the plate is the whole of what the cooking model changed,
 * and a kitchen with one crate in it cannot teach it. `meat_and_veg` leads the
 * pool, so the ticket waiting on the board from the first tick is the one the
 * lesson builds.
 *
 * The pan starts on the counter rather than on the stove, because "the vessel
 * goes on the heat, the food goes in the vessel" is a rule the tutorial teaches
 * and a player cannot practise a rule that has already been applied for them.
 */
export const TUTORIAL_MAP_TEXT = `
// The tutorial kitchen — 6 wide x 8 high, one seat.
//
// orders: meat_and_veg, fried_meat
// container: pan @ 0,4
W W W W W W
M . . . . A
N . . . . D
C . . . . S
X . . . . K
# . . . . E
R . 1 . . W
W W W W W W
`.trim()

export const TUTORIAL_MAP_NAME = 'tutorial'

export const tutorialMap: MapContent = parseMap(TUTORIAL_MAP_TEXT, TUTORIAL_MAP_NAME)

/**
 * Two dishes, and the shortest complete loop the game has: crate, board, pan,
 * heat, plate, hatch, sink, plate return. Everything a round will ask of the
 * player is in it, fire included — a pan left on the heat burns and then sets
 * the stove alight here exactly as it does in the kitchen next door.
 *
 * `meat_and_veg` is first, and it is first on purpose. A pan cooks one thing at
 * a time, so a two-item dish is two cooks that meet on a plate — the single
 * biggest change to how this game is played, and the one a player who has only
 * ever fried one piece of meat would walk into a round without. It leads the
 * dish list, which is what makes it the lead ticket, the route on the closing
 * card, and the dish the arc actually builds.
 */
const TUTORIAL_RECIPES: RecipeContent = {
  ingredients: {
    meat: { label: 'Meat', raw: true },
    chopped_meat: { label: 'Chopped meat', raw: true },
    fried_meat: { label: 'Fried meat', raw: false },
    carrot: { label: 'Carrot', raw: true },
    chopped_carrot: { label: 'Chopped carrot', raw: true },
    stewed_carrot: { label: 'Stewed carrot', raw: false },
  },
  containers: {
    // The shipped pan, capacity and all. It holds one ingredient here because
    // it holds one next door: the practice kitchen drifting from the real one
    // is exactly how a tutorial starts lying, and "the pan takes one thing"
    // is the rule a learner most needs to arrive with.
    pan: { label: 'Pan', capacity: 1, equipment: ['stove', 'oven'] },
  },
  equipment: {
    extinguisher: { label: 'Fire extinguisher', rack: 'extinguisher_rack' },
  },
  transforms: [
    { station: 'board', from: 'meat', to: 'chopped_meat', durationKey: 'chopMs' },
    { station: 'board', from: 'carrot', to: 'chopped_carrot', durationKey: 'chopVegMs' },
  ],
  cookable: [
    {
      station: 'stove',
      container: 'pan',
      from: 'chopped_meat',
      to: 'fried_meat',
      toDoneKey: 'panCookToDoneMs',
      toBurntKey: 'doneToBurntMs',
      toFireKey: 'burntToFireMs',
    },
    {
      station: 'stove',
      container: 'pan',
      from: 'chopped_carrot',
      to: 'stewed_carrot',
      toDoneKey: 'stewToDoneMs',
      toBurntKey: 'stewToBurntMs',
      toFireKey: 'burntToFireMs',
    },
  ],
  dishes: [
    { id: 'meat_and_veg', label: 'Meat & veg', contents: ['fried_meat', 'stewed_carrot'] },
    { id: 'fried_meat', label: 'Fried meat', contents: ['fried_meat'] },
  ],
  orderPool: ['meat_and_veg', 'fried_meat'],
}

/**
 * Learner's clocks.
 *
 * Every duration is a fraction of the shipped one, because a tutorial that
 * spends twelve seconds frying is a tutorial nobody finishes — but they are
 * still *clocks*, so the lesson "the bar fills, then a second clock starts" is
 * the real lesson and not a mime of it.
 *
 * The ticket queue is the one thing tuned rather than shortened: one ticket,
 * arriving immediately, valid for an hour. A fuse burning down while somebody
 * reads the screen would teach urgency before it has taught cooking, and a
 * ticket that lapsed mid-lesson would leave the serve step with nothing to
 * serve into.
 */
const TUTORIAL_TIMINGS: TimingsContent = {
  roundLengthMs: 60 * 60 * 1000,
  // An hour of slack, never decaying: the ticket outlives the lesson whatever
  // dish it names, which is the same "valid for an hour" the comment above
  // describes, restated in the budget the engine now uses.
  orderSlackMs: 60 * 60 * 1000,
  orderSlackDecayMs: 0,
  minOrderSlackMs: 60 * 60 * 1000,
  orderSpawnIntervalMs: 60 * 60 * 1000,
  maxActiveOrders: 1,
  firstOrderDelayMs: 0,

  chopMs: 1200,
  chopVegMs: 1200,
  panCookToDoneMs: 4000,
  doneToBurntMs: 4000,
  stewToDoneMs: 4000,
  stewToBurntMs: 4000,
  ovenBakeToDoneMs: 4000,
  ovenBakeToBurntMs: 4000,
  burntToFireMs: 3000,
  extinguishMs: 1500,

  washMs: 1600,
  plateReturnDelayMs: 1500,
  platesPerMap: 3,
  // The shipped bound, not a shortened one: how much a plate holds is a rule
  // rather than a clock, and the tutorial teaches the rules unchanged.
  plateCapacity: 3,

  readyTimeoutMs: 15000,
  voiceReadyGraceMs: 6000,
  // No agent sits in this kitchen, so pacing is inert here; the shipped value.
  agentMoveIntervalMs: 150,
  // Nobody solves this kitchen either; the shipped oracle pace, so the copy
  // stays a copy.
  oracleStepMs: 180,

  scoring: {
    baseScorePerOrder: 20,
    maxSpeedBonus: 6,
    expiredOrderPenalty: -10,
    unmatchedServePenalty: 0,
  },

  latency: {
    gateSampleCount: 12,
    gateSampleTimeoutMs: 2000,
    gateMaxMedianRttMs: 250,
    voiceSampleIntervalMs: 5000,
  },
}

export const TUTORIAL_CONTENT: Content = {
  map: tutorialMap,
  maps: { [TUTORIAL_MAP_NAME]: tutorialMap },
  recipes: TUTORIAL_RECIPES,
  timings: TUTORIAL_TIMINGS,
}

/**
 * The shipped knobs, restated — for the practice round only.
 *
 * The teaching steps run on learner's clocks because a lesson is not a test.
 * The practice round after them is the opposite: it exists so that the first
 * time a player meets a five-minute clock, a ticket that can lapse and a pan
 * that will catch fire, they are on their own rather than beside a stranger on
 * a headset. It is therefore played under the *shipped* numbers, not shortened
 * ones — a practice round that is easier than the game has practised nothing.
 *
 * These are a copy, and a copy is a drift risk, so it is a gated one. The
 * server deliberately serves the browser a catalogue and no clocks — that is
 * what stops a mis-tuned client drawing a bar that lies — so there is no
 * request that could fetch them and no bundle-time import that would not drag
 * the filesystem loader into the browser. What there is instead is
 * `client/test/tutorialSandbox.test.ts`, which reads the real
 * `server/src/content/timings.json` and fails if a single knob here disagrees
 * with it. Retuning the game is still one edit in one file; it is just an edit
 * this table has to be brought along with, and the build says so.
 */
const PRACTICE_TIMINGS: TimingsContent = {
  roundLengthMs: 300000,
  orderSlackMs: 60000,
  orderSlackDecayMs: 3000,
  minOrderSlackMs: 20000,
  orderSpawnIntervalMs: 35000,
  maxActiveOrders: 4,
  firstOrderDelayMs: 0,

  chopMs: 3000,
  chopVegMs: 2000,

  panCookToDoneMs: 12000,
  doneToBurntMs: 20000,
  stewToDoneMs: 15000,
  stewToBurntMs: 12000,
  ovenBakeToDoneMs: 18000,
  ovenBakeToBurntMs: 22000,
  burntToFireMs: 18000,
  extinguishMs: 2500,

  washMs: 4000,
  plateReturnDelayMs: 5000,
  platesPerMap: 3,
  plateCapacity: 3,

  readyTimeoutMs: 15000,
  voiceReadyGraceMs: 6000,
  // No agent sits in this kitchen, so pacing is inert here; the shipped value.
  agentMoveIntervalMs: 150,
  // Nobody solves this kitchen either; the shipped oracle pace, so the copy
  // stays a copy.
  oracleStepMs: 180,

  scoring: {
    baseScorePerOrder: 20,
    maxSpeedBonus: 6,
    expiredOrderPenalty: -10,
    unmatchedServePenalty: 0,
  },

  latency: {
    gateSampleCount: 12,
    gateSampleTimeoutMs: 2000,
    gateMaxMedianRttMs: 250,
    voiceSampleIntervalMs: 5000,
  },
}

/**
 * The same kitchen and the same recipe book, on the real clocks.
 *
 * One field differs from `TUTORIAL_CONTENT`, and that is the whole design of
 * the practice round: it is not a second game, it is the lesson's own kitchen
 * with the training wheels taken off. Same map, so nothing has moved; same
 * dishes, so nothing is new to read; a round clock, a ticket queue that
 * tightens per slot, and a pan that will burn and then catch fire if it is
 * left — because those are the three things the steps cannot teach without
 * ceasing to be steps.
 */
export const PRACTICE_CONTENT: Content = {
  map: tutorialMap,
  maps: { [TUTORIAL_MAP_NAME]: tutorialMap },
  recipes: TUTORIAL_RECIPES,
  timings: PRACTICE_TIMINGS,
}

/**
 * The tutorial kitchen described the way `/api/config` describes a real one.
 *
 * `stepsFor` builds its prose and its pictures from a `ClientConfig`, and the
 * gate now teaches the sandbox rather than the map the round will use — so it
 * has to be handed the sandbox's own config, or it would explain a carrot the
 * player cannot pick up. Built with `dishTicket`, `ingredientCatalogue` and
 * `orderPoolFor`: the same three functions the server's `/api/config` handler
 * calls, so the shape is the server's shape and not an approximation of it.
 *
 * The lobby variant of the tutorial keeps using the real config. That one is a
 * reading card about the kitchen actually being played, and nothing on it is
 * interactive.
 */
export const TUTORIAL_CONFIG: ClientConfig = {
  latency: TUTORIAL_TIMINGS.latency,
  playerCount: 1,
  maps: [
    {
      name: tutorialMap.name,
      width: tutorialMap.width,
      height: tutorialMap.height,
      orderPool: orderPoolFor(TUTORIAL_CONTENT, tutorialMap),
      seats: tutorialMap.spawns.length,
    },
  ],
  defaultMap: tutorialMap.name,
  dishes: TUTORIAL_RECIPES.dishes.map((dish) => ({
    id: dish.id,
    label: dish.label,
    contents: [...dish.contents],
    requires: dishTicket(TUTORIAL_RECIPES, dish),
  })),
  ingredients: ingredientCatalogue(TUTORIAL_RECIPES),
  readyTimeoutMs: TUTORIAL_TIMINGS.readyTimeoutMs,
  voiceReadyGraceMs: TUTORIAL_TIMINGS.voiceReadyGraceMs,
}
